import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTripById, reviewTrip } from '../services/tripService';
import { GENDER } from '../constants/enums';

const ACTION_APPROVE = 'approve';
const ACTION_REJECT = 'reject';

const STATUS_AFTER_ACTION = {
  [ACTION_APPROVE]: 'published',
  [ACTION_REJECT]: 'rejected',
};

const genderMap = GENDER;

const TRIP_STATUS_DISPLAY = {
  draft: { label: 'Draft', className: 'pending' },
  generated: { label: 'Generated', className: 'active' },
  pending: { label: 'Pending', className: 'pending' },
  pending_approval: { label: 'Pending', className: 'pending' },
  accepted: { label: 'Accepted', className: 'success' },
  published: { label: 'Accepted', className: 'success' },
  rejected: { label: 'Rejected', className: 'failed' },
  closed: { label: 'Closed', className: 'closed' },
};

const APPLICATION_STATUS_DISPLAY = {
  applied: { label: 'Applied', className: 'pending' },
  approved: { label: 'Approved', className: 'success' },
  rejected: { label: 'Rejected', className: 'failed' },
};

const APPLICATION_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'applied', label: 'Applied' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const FLIGHT_TYPE_LABELS = {
  private_jet: 'Private Jet',
  commercial: 'Commercial',
  none: 'None',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return `${formatDate(dateStr)} · ${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })}`;
}

function calcAge(dateOfBirth) {
  if (!dateOfBirth) return '—';
  const diff = Date.now() - new Date(dateOfBirth).getTime();
  const age = Math.floor(diff / 31557600000);
  return age > 0 && age < 150 ? age : '—';
}

function getTripStatusDisplay(status) {
  const key = String(status || '').toLowerCase();
  return TRIP_STATUS_DISPLAY[key] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    className: key || 'pending',
  };
}

function getApplicationStatusDisplay(status) {
  const key = String(status || '').toLowerCase();
  return APPLICATION_STATUS_DISPLAY[key] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    className: key || 'pending',
  };
}

function yesNo(value) {
  return value ? 'Yes' : 'No';
}

function isPendingStatus(status) {
  const key = String(status || '').toLowerCase();
  return key === 'pending' || key === 'pending_approval';
}

export default function TripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDays, setOpenDays] = useState({});
  const [reviewAction, setReviewAction] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadTrip() {
      setLoading(true);
      setError('');
      try {
        const response = await getTripById(tripId);
        if (cancelled) return;
        setTrip(response.data || response || null);
        setOpenDays({});
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load trip details.');
        setTrip(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTrip();
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  const applicants = useMemo(
    () => (Array.isArray(trip?.applicants) ? trip.applicants : []),
    [trip]
  );

  const counts = useMemo(() => {
    const next = { all: applicants.length, applied: 0, approved: 0, rejected: 0 };
    applicants.forEach((app) => {
      const key = String(app.status || '').toLowerCase();
      if (key === 'applied') next.applied += 1;
      if (key === 'approved') next.approved += 1;
      if (key === 'rejected') next.rejected += 1;
    });
    return next;
  }, [applicants]);

  const filteredApplicants = useMemo(() => {
    if (statusFilter === 'all') return applicants;
    return applicants.filter(
      (app) => String(app.status || '').toLowerCase() === statusFilter
    );
  }, [applicants, statusFilter]);

  const photos = useMemo(() => {
    const list = Array.isArray(trip?.photos) ? [...trip.photos] : [];
    return list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [trip]);

  const itineraryDays = useMemo(() => {
    const list = Array.isArray(trip?.itineraryDays) ? [...trip.itineraryDays] : [];
    return list.sort((a, b) => (a.dayNumber ?? 0) - (b.dayNumber ?? 0));
  }, [trip]);

  const closePhotoViewer = useCallback(() => setPhotoIndex(null), []);

  const showPrevPhoto = useCallback(() => {
    setPhotoIndex((prev) => (prev == null ? prev : (prev - 1 + photos.length) % photos.length));
  }, [photos.length]);

  const showNextPhoto = useCallback(() => {
    setPhotoIndex((prev) => (prev == null ? prev : (prev + 1) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (photoIndex == null) return undefined;

    function onKeyDown(e) {
      if (e.key === 'Escape') closePhotoViewer();
      if (e.key === 'ArrowLeft') showPrevPhoto();
      if (e.key === 'ArrowRight') showNextPhoto();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [photoIndex, closePhotoViewer, showPrevPhoto, showNextPhoto]);

  function toggleDay(dayId) {
    setOpenDays((prev) => ({ ...prev, [dayId]: !prev[dayId] }));
  }

  function openReviewModal(action) {
    setRejectReason('');
    setReviewAction(action);
  }

  function closeReviewModal() {
    if (reviewLoading) return;
    setReviewAction(null);
    setRejectReason('');
  }

  async function confirmTripAction() {
    if (!reviewAction || reviewLoading) return;
    setReviewLoading(true);
    try {
      const reason = reviewAction === ACTION_REJECT ? rejectReason.trim() : '';
      await reviewTrip(tripId, {
        action: reviewAction,
        ...(reason ? { reason } : {}),
      });
      const nextStatus = STATUS_AFTER_ACTION[reviewAction];
      setTrip((prev) =>
        prev
          ? {
              ...prev,
              status: nextStatus,
              ...(reviewAction === ACTION_REJECT && reason ? { rejectionReason: reason } : {}),
            }
          : prev
      );
      setReviewAction(null);
      setRejectReason('');
    } catch (err) {
      alert(err.message || `Failed to ${reviewAction === ACTION_APPROVE ? 'accept' : 'reject'} trip.`);
    } finally {
      setReviewLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="trip-detail-page main-bg-top">
        <div className="user-detail-loading">Loading trip details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trip-detail-page main-bg-top">
        <div className="user-detail-error">
          <p>{error}</p>
          <button className="btn btn-primary" type="button" onClick={() => navigate('/trips')}>
            Back to Trips
          </button>
        </div>
      </div>
    );
  }

  if (!trip) return null;

  const tripStatus = getTripStatusDisplay(trip.status);
  const host = trip.host || {};
  const hostName = host.name || '—';
  const hostEmail = host.email || '';
  const hostImage = host.photo || '';
  const flightLabel = FLIGHT_TYPE_LABELS[trip.flightType] || trip.flightType || '—';
  const canReview = isPendingStatus(trip.status);
  const activePhoto = photoIndex != null ? photos[photoIndex] : null;

  return (
    <div className="trip-detail-page main-bg-top">
      <div className="coupons-top-bar">
        <button className="coupons-back-btn" type="button" onClick={() => navigate('/trips')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      <div className="user-detail-content">
        <div className="user-detail-card">
          <div className="trip-detail-summary">
            <div>
              <h3 className="trip-detail-title">{trip.title || 'Untitled Trip'}</h3>
              <p className="trip-detail-destination">
                <img
                  alt=""
                  className="menu-icon-img"
                  src={`${process.env.PUBLIC_URL}/images/location-icon.svg`}
                />
                {trip.destination || '—'}
              </p>
              {trip.itineraryNotes ? (
                <p className="trip-detail-description">{trip.itineraryNotes}</p>
              ) : null}
            </div>
            <div className="trip-detail-badges">
              <div className="trip-detail-badge-row">
                <span className={`status-badge status-${tripStatus.className}`}>{tripStatus.label}</span>
                {trip.isArchived ? (
                  <span className="status-badge status-refunded">Archived</span>
                ) : null}
              </div>
              {String(trip.status || '').toLowerCase() === 'rejected' && trip.rejectionReason ? (
                <p className="trip-rejection-reason">
                  <span className="trip-rejection-reason-label">Reason for Rejection :</span>{' '}
                  <span className="trip-rejection-reason-text">{trip.rejectionReason}</span>
                </p>
              ) : null}
              {canReview ? (
                <div className="trip-detail-actions">
                  <button
                    type="button"
                    className="trip-action-btn trip-action-accept"
                    onClick={() => openReviewModal(ACTION_APPROVE)}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="trip-action-btn trip-action-reject"
                    onClick={() => openReviewModal(ACTION_REJECT)}
                  >
                    Reject
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="detail-grid trip-detail-meta">
            <div className="detail-item">
              <span className="detail-label">Host</span>
              <span className="detail-value">
                <div className="user-cell">
                  <div className="user-cell-avatar">
                    {hostImage ? (
                      <img src={hostImage} alt={hostName} />
                    ) : (
                      <span className="avatar-placeholder">
                        {hostName !== '—' ? hostName.charAt(0).toUpperCase() : '?'}
                      </span>
                    )}
                  </div>
                  <div className="user-cell-info">
                    <span className="user-cell-name">{hostName}</span>
                    <span className="user-cell-email">{hostEmail}</span>
                  </div>
                </div>
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Host Location</span>
              <span className="detail-value">{host.locationCity || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Start Date</span>
              <span className="detail-value">{formatDate(trip.startDate)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">End Date</span>
              <span className="detail-value">{formatDate(trip.endDate)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Main Event</span>
              <span className="detail-value">{trip.mainEvent || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Accommodation</span>
              <span className="detail-value">{trip.accommodation || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Flight Type</span>
              <span className="detail-value">{flightLabel}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Instagram</span>
              <span className="detail-value">{trip.instagramHandle || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Flights Included</span>
              <span className="detail-value">{yesNo(trip.flightsIncluded)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Stay Included</span>
              <span className="detail-value">{yesNo(trip.stayIncluded)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Concierge Included</span>
              <span className="detail-value">{yesNo(trip.conciergeIncluded)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Application Cap</span>
              <span className="detail-value">
                {trip.applicationCount ?? 0}
                {trip.applicationCap != null ? ` / ${trip.applicationCap}` : ''}
              </span>
            </div>
          </div>
        </div>

        {photos.length > 0 && (
          <div className="user-detail-card">
            <h4 className="detail-card-title">Photos</h4>
            <div className="user-images-grid">
              {photos.map((photo, index) => (
                <button
                  key={photo.id}
                  type="button"
                  className="user-image-item trip-photo-item"
                  onClick={() => setPhotoIndex(index)}
                  aria-label={`View photo ${index + 1} of ${photos.length}`}
                >
                  <img src={photo.url} alt={photo.isHero ? 'Hero' : 'Trip'} />
                  {photo.isHero ? <span className="trip-photo-badge">Hero</span> : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {itineraryDays.length > 0 && (
          <div className="user-detail-card">
            <h4 className="detail-card-title">Itinerary</h4>
            <div className="trip-itinerary-list">
              {itineraryDays.map((day) => {
                const isOpen = Boolean(openDays[day.id]);
                const items = Array.isArray(day.items)
                  ? [...day.items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                  : [];

                return (
                  <div key={day.id} className={`trip-itinerary-day${isOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="trip-itinerary-day-toggle"
                      onClick={() => toggleDay(day.id)}
                      aria-expanded={isOpen}
                    >
                      <span className="trip-itinerary-day-label">
                        Day {day.dayNumber}
                        {day.title ? `: ${day.title}` : ''}
                        <span className="trip-itinerary-tooltip" role="tooltip">
                          Click to see full details
                        </span>
                      </span>
                      <svg
                        className="trip-itinerary-chevron"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="trip-itinerary-day-body">
                        {items.length === 0 ? (
                          <p className="trip-itinerary-empty">No activities for this day.</p>
                        ) : (
                          items.map((item) => (
                            <div key={item.id} className="trip-itinerary-item">
                              <strong>{item.title || '—'}</strong>
                              {item.details ? <p>{item.details}</p> : null}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="trip-applicant-stats">
          <div className="trip-stat-card">
            <span className="trip-stat-label">Applied</span>
            <span className="trip-stat-value">{counts.applied}</span>
          </div>
          <div className="trip-stat-card">
            <span className="trip-stat-label">Approved</span>
            <span className="trip-stat-value">{counts.approved}</span>
          </div>
          <div className="trip-stat-card">
            <span className="trip-stat-label">Rejected</span>
            <span className="trip-stat-value">{counts.rejected}</span>
          </div>
          <div className="trip-stat-card">
            <span className="trip-stat-label">Total</span>
            <span className="trip-stat-value">{counts.all}</span>
          </div>
        </div>

        <div className="user-detail-card">
          <div className="trip-applicants-header">
            <h4 className="detail-card-title">Applicants</h4>
            <div className="trip-applicant-filters">
              {APPLICATION_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`chart-tab${statusFilter === filter.key ? ' active' : ''}`}
                  onClick={() => setStatusFilter(filter.key)}
                >
                  {filter.label} ({counts[filter.key] ?? 0})
                </button>
              ))}
            </div>
          </div>

          <div className="users-table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Gender</th>
                  <th>Age</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Applied On</th>
                  <th>Decided On</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplicants.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="users-empty">
                      No {statusFilter === 'all' ? '' : `${statusFilter} `}applicants found.
                    </td>
                  </tr>
                ) : (
                  filteredApplicants.map((app) => {
                    const user = app.user || {};
                    const name = user.name || '—';
                    const email = user.email || '';
                    const image = user.photo || '';
                    const appStatus = getApplicationStatusDisplay(app.status);

                    return (
                      <tr key={app.applicationId || `${user.id}-${app.status}-${app.appliedAt}`}>
                        <td>
                          <div className="user-cell">
                            <div className="user-cell-avatar">
                              {image ? (
                                <img
                                  src={image}
                                  alt={name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.textContent = name !== '—' ? name.charAt(0).toUpperCase() : '?';
                                  }}
                                />
                              ) : (
                                <span className="avatar-placeholder">
                                  {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
                                </span>
                              )}
                            </div>
                            <div className="user-cell-info">
                              <span className="user-cell-name">{name}</span>
                              <span className="user-cell-email">{email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{genderMap[user.gender] || '—'}</td>
                        <td>{calcAge(user.dateOfBirth)}</td>
                        <td>
                          <div className="location-cell">
                            <img
                              alt=""
                              className="menu-icon-img"
                              src={`${process.env.PUBLIC_URL}/images/location-icon.svg`}
                            />
                            {user.locationCity || '—'}
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge status-${appStatus.className}`}>
                            {appStatus.label}
                          </span>
                        </td>
                        <td>{formatDateTime(app.appliedAt)}</td>
                        <td>{formatDateTime(app.decidedAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {activePhoto && (
        <div className="modal-overlay" onClick={closePhotoViewer}>
          <div
            className="modal-content trip-photo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="trip-photo-modal-header">
              <span className="trip-photo-modal-count">
                {photoIndex + 1} / {photos.length}
              </span>
              <button
                type="button"
                className="trip-photo-modal-close"
                aria-label="Close"
                onClick={closePhotoViewer}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="trip-photo-modal-stage">
              {photos.length > 1 && (
                <button
                  type="button"
                  className="trip-photo-nav trip-photo-nav-prev"
                  aria-label="Previous photo"
                  onClick={showPrevPhoto}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
              )}

              <img
                src={activePhoto.url}
                alt={activePhoto.isHero ? 'Hero' : `Trip photo ${photoIndex + 1}`}
                className="trip-photo-modal-image"
              />

              {photos.length > 1 && (
                <button
                  type="button"
                  className="trip-photo-nav trip-photo-nav-next"
                  aria-label="Next photo"
                  onClick={showNextPhoto}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {reviewAction && (
        <div className="modal-overlay" onClick={closeReviewModal}>
          <div
            className={`modal-content trip-review-modal ${reviewAction === ACTION_REJECT ? 'is-reject' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-title">
              {reviewAction === ACTION_APPROVE ? 'Accept Trip?' : 'Reject Trip?'}
            </h3>
            <p className="modal-message">
              Are you sure you want to {reviewAction === ACTION_APPROVE ? 'accept' : 'reject'}{' '}
              {trip.title || 'this trip'}?
            </p>

            {reviewAction === ACTION_REJECT ? (
              <div className="trip-reject-reason">
                <label htmlFor="trip-detail-reject-reason" className="trip-reject-reason-label">
                  Reason <span>(optional)</span>
                </label>
                <textarea
                  id="trip-detail-reject-reason"
                  className="trip-reject-reason-input"
                  rows={4}
                  placeholder="e.g. Photos don't meet our guidelines."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  disabled={reviewLoading}
                />
              </div>
            ) : null}

            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                disabled={reviewLoading}
                onClick={closeReviewModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`modal-btn modal-btn-confirm ${reviewAction === ACTION_REJECT ? 'modal-btn-danger' : ''}`}
                disabled={reviewLoading}
                onClick={confirmTripAction}
              >
                {reviewLoading
                  ? 'Saving...'
                  : reviewAction === ACTION_APPROVE
                    ? 'Accept'
                    : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
